/**
 * Shared popover positioning: opens below the trigger by default, flips above
 * when there is not enough room, and clamps inside the viewport on every axis.
 * Used by chat/skills, user menu, theme and language selectors so anchored
 * menus never render off-screen on small or rotated viewports.
 */

export interface PopoverPosition {
  top: number;
  left: number;
  right: number;
}

export interface PopoverViewport {
  width: number;
  height: number;
}

const MARGIN = 8;
const GAP = 8;

/**
 * Compute on-screen coordinates for a menu anchored to a trigger rect.
 *
 * @param triggerRect Trigger bounding rect (viewport coordinates).
 * @param menuWidth   Measured menu width in px.
 * @param menuHeight  Measured menu height in px.
 * @param align       "start" aligns the menu's left edge with the trigger's
 *                    left; "end" aligns the right edges (right-anchored menus);
 *                    "center" centers the menu horizontally over the trigger.
 * @param viewport    Optional explicit viewport (defaults to window).
 */
export function computePopoverPosition(
  triggerRect: Pick<DOMRect, "top" | "bottom" | "left" | "right">,
  menuWidth: number,
  menuHeight: number,
  align: "start" | "end" | "center" = "start",
  viewport?: PopoverViewport
): PopoverPosition {
  const vw = viewport?.width ?? window.innerWidth;
  const vh = viewport?.height ?? window.innerHeight;
  const margin = MARGIN;
  const gap = GAP;

  const triggerWidth = triggerRect.right - triggerRect.left;
  const centerLeft = triggerRect.left + (triggerWidth - menuWidth) / 2;

  // Horizontal: keep the full menu width on-screen, anchored to the trigger.
  const left =
    align === "end"
      ? Math.max(margin, Math.min(triggerRect.right - menuWidth, vw - menuWidth - margin))
      : align === "center"
        ? Math.max(margin, Math.min(centerLeft, vw - menuWidth - margin))
        : Math.max(margin, Math.min(triggerRect.left, vw - menuWidth - margin));

  // Vertical: prefer below; flip above when below would overflow.
  let top = triggerRect.bottom + gap;
  if (top + menuHeight > vh - margin) {
    top = triggerRect.top - menuHeight - gap;
  }
  // Menus taller than the viewport get pinned inside instead of overflowing.
  if (menuHeight > vh - margin * 2) {
    top = margin;
  } else if (top < margin) {
    top = Math.max(margin, Math.min(triggerRect.top + gap, vh - menuHeight - margin));
  }

  return { top, left, right: vw - left - menuWidth };
}
